import React, { useRef, useState, useEffect } from "react";
import { View, Text, Pressable, Animated, Platform } from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { useTheme } from "../../../theme";
import { TicketingLandingAction } from "../../../store/slices/ticketingPortalSlice";
import styles from "../TicketingPortalScreen.style";
import mobileStyles from "../TicketingPortalScreen.mobile.style";

type ActionWithBadge = TicketingLandingAction & {
  badgeText?: string;
};

type Props = {
  action: ActionWithBadge;
  onPress?: () => void;
  mobile?: boolean;
};

const ActionTile: React.FC<Props> = ({ action, onPress, mobile }) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const [hovered, setHovered] = useState(false);
  const arrowTranslate = useRef(new Animated.Value(0)).current;
  const shimmerAnim = useRef(new Animated.Value(0.4)).current;

  useEffect(() => {
    if (!action.metricLoading) {
      shimmerAnim.setValue(0.4);
      return;
    }
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(shimmerAnim, { toValue: 1, duration: 600, useNativeDriver: true }),
        Animated.timing(shimmerAnim, { toValue: 0.4, duration: 600, useNativeDriver: true }),
      ])
    );
    loop.start();
    return () => loop.stop();
  }, [action.metricLoading, shimmerAnim]);

  const setHoverState = (nextState: boolean) => {
    setHovered(nextState);
    Animated.timing(arrowTranslate, {
      toValue: nextState ? 1 : 0,
      duration: 160,
      useNativeDriver: true,
    }).start();
  };

  /* ── Compact horizontal tile for iPhone ───────────────── */
  if (mobile) {
    return (
      <Pressable
        onPress={onPress}
        style={[
          mobileStyles.tileCard,
          isDark && {
            backgroundColor: colors.background.surface,
            borderColor: colors.border.default,
            borderWidth: 1,
          },
        ]}
      >
        <View
          style={[
            mobileStyles.tileIconWrap,
            {
              backgroundColor: isDark
                ? "rgba(61, 139, 255, 0.18)"
                : action.iconBackgroundColor || "#e8f0fb",
            },
          ]}
        >
          <MaterialIcons
            name={action.icon || "dashboard"}
            size={22}
            color={isDark ? colors.brand.primary : action.iconColor || "#156fd5"}
          />
        </View>

        <View style={mobileStyles.tileBody}>
          <View style={mobileStyles.tileTitleRow}>
            <Text style={[mobileStyles.tileTitle, isDark && { color: colors.text.primary }]}>
              {action.title}
            </Text>
            {action.badgeText ? (
              <Text
                style={[
                  mobileStyles.tileBadge,
                  isDark && {
                    backgroundColor: "rgba(61, 139, 255, 0.22)",
                    color: "#99CFFF",
                  },
                  action.id === "sustain-queue"
                    ? isDark
                      ? { backgroundColor: "rgba(220, 53, 53, 0.22)", color: "#FFB09A" }
                      : mobileStyles.tileBadgeAlert
                    : null,
                ]}
              >
                {action.badgeText}
              </Text>
            ) : null}
          </View>
          <Text
            style={[mobileStyles.tileDescription, isDark && { color: colors.text.secondary }]}
            numberOfLines={2}
          >
            {action.description}
          </Text>
        </View>

        <View
          style={[
            mobileStyles.tileArrowWrap,
            isDark && { backgroundColor: colors.background.elevated },
          ]}
        >
          <MaterialIcons name="chevron-right" size={18} color={isDark ? colors.text.secondary : "#9099aa"} />
        </View>
      </Pressable>
    );
  }

  /* ── Default vertical tile (web / iPad) ───────────────── */
  return (
    <Pressable
      onPress={onPress}
      onHoverIn={() => setHoverState(true)}
      onHoverOut={() => setHoverState(false)}
      style={[
        styles.tileCard,
        isDark && {
          backgroundColor: colors.background.surface,
          borderColor: hovered ? colors.brand.primary : colors.border.default,
          borderWidth: 1,
        },
        hovered ? styles.tileCardHovered : styles.tileCardDefault,
      ]}
    >
      <View
        style={[
          styles.tileIconWrap,
          {
            backgroundColor: isDark
              ? "rgba(61, 139, 255, 0.18)"
              : action.iconBackgroundColor || "#e8f0fb",
          },
        ]}
      >
        <MaterialIcons
          name={action.icon || "dashboard"}
          size={24}
          color={isDark ? colors.brand.primary : action.iconColor || "#156fd5"}
        />
      </View>

      <View style={styles.tileTitleRow}>
        <Text style={[styles.tileTitle, isDark && { color: colors.text.primary }]}>{action.title}</Text>
        {action.badgeText ? (
          <Text
            style={[
              styles.tileBadge,
              isDark && {
                backgroundColor: "rgba(61, 139, 255, 0.22)",
                color: "#99CFFF",
              },
              action.id === "sustain-queue"
                ? isDark
                  ? { backgroundColor: "rgba(220, 53, 53, 0.22)", color: "#FFB09A" }
                  : styles.tileBadgeAlert
                : null,
            ]}
          >
            {action.badgeText}
          </Text>
        ) : null}
      </View>

      <Text style={[styles.tileDescription, isDark && { color: colors.text.secondary }]}>{action.description}</Text>

      <View style={styles.tileFooter}>
        {action.metricLoading ? (
          <Animated.View
            style={{
              opacity: shimmerAnim,
              width: 72,
              height: 14,
              borderRadius: 4,
              backgroundColor: isDark ? colors.background.elevated : "#d1d5db",
            }}
          />
        ) : (
          <Text
            style={[
              styles.tileMetric,
              isDark && { color: colors.text.secondary },
              action.id === "my-tickets"
                ? isDark
                  ? { color: "#FFD166" }
                  : styles.tileMetricAlert
                : null,
            ]}
          >
            {action.metric}
          </Text>
        )}

        <Animated.View
          style={{
            transform: [
              {
                translateX: arrowTranslate.interpolate({
                  inputRange: [0, 1],
                  outputRange: [0, 3],
                }),
              },
            ],
          }}
        >
          <View
            style={[
              styles.tileArrowCircle,
              isDark && { backgroundColor: colors.background.elevated },
              hovered ? (isDark ? { backgroundColor: colors.brand.primary } : styles.tileArrowCircleHover) : null,
            ]}
          >
            <MaterialIcons
              name="chevron-right"
              size={18}
              color={hovered ? "#ffffff" : isDark ? colors.text.secondary : "#9099aa"}
            />
          </View>
        </Animated.View>
      </View>
    </Pressable>
  );
};

export default ActionTile;
